<template>
  <div class="country-selector critical-component-hide">
    <button
      class="country-selector__close icon-button"
      type="button"
      :aria-label="$string('accessibility.close')"
      @click="handleCloseOverlay"
    >
      <slot name="close">
        <icon-close />
      </slot>
    </button>

    <!-- Body -->
    <div class="country-selector__body">
      <div
        v-if="!showPrompt"
        class="country-selector__form"
      >
        <div
          class="
            country-selector__title
            text-heading-xs
            text-heading-2xs-desktop
          "
          v-text="$string('general.country_selector.selector_title')"
        />
        <div class="country-selector__form-wrapper mt-l mt-xl-desktop">
          <div class="country-selector__form-group form-group">
            <label
              class="text-heading-3xs"
              for="selected-country"
              v-text="$string('general.country_selector.selector_country_label')"
            />
            <div class="country-selector__form-group-country">
              <span
                class="country-selector-btn__flag"
              >
                <responsive-image
                  v-if="getSelectedFlag"
                  :image="{url:getSelectedFlag}"
                  min-max="32-40"
                  ratio="1:1"
                />
              </span>
              <select
                v-if="locales"
                id="selected-country"
                name="selected_country_iso_code"
                @change="handleLocaleSelect"
              >
                <option
                  v-for="country of locales"
                  :key="`country-${country.isoCode}`"
                  :value="country.isoCode"
                  :aria-current="(
                    getSelectedState(country.isoCode, selectedCountryIsoCode.toUpperCase())
                  )"
                  :selected="(
                    getSelectedState(country.isoCode, selectedCountryIsoCode.toUpperCase())
                  )"
                >
                  {{ country.name }} ({{ country.currency.isoCode }} {{ country.currency.symbol }})
                </option>
              </select>
            </div>
          </div>

          <div class="country-selector__form-group form-group">
            <label
              class="text-heading-3xs"
              for="selected-language"
              v-text="$string('general.country_selector.selector_language_label')"
            />
            <select
              v-if="availableLanguages"
              id="selected-language"
              name="selected_language"
              @change="handleLocaleSelect"
            >
              <option
                v-for="language of availableLanguages"
                :key="`lang-${language.isoCode}`"
                :value="language.isoCode"
                :aria-current="(getSelectedState(language.isoCode, selectedLanguage.toUpperCase()))"
                :selected="(getSelectedState(language.isoCode, selectedLanguage.toUpperCase()))"
              >
                {{ language.endonymName }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div
        v-else
        class="country-selector__prompt"
      >
        <div class="country-selector__prompt-header">
          <div
            class="
              country-selector__prompt-title
              text-heading-2xs
              text-heading-xs-desktop
            "
            v-text="$string('general.country_selector.prompt_title',
                            { replace: { country: suggestedCountryNameFormatted }}
            )"
          />
        </div>

        <p
          v-if="$string('general.country_selector.prompt_suggestion') !== 'No string found'"
          class="country-selector__prompt-content-subtitle"
          v-text="$string('general.country_selector.prompt_suggestion',
                          { replace: { country: suggestedCountryNameFormatted } }
          )"
        />

        <ul
          v-if="matchSuggestedMarket"
          class="country-selector__prompt-content mt-l mt-xl-desktop"
        >
          <li
            v-if="$string('general.country_selector.prompt_currency') !== 'No string found'"
            class="text-body-l-regular text-body-xl-regular-desktop"
            v-text="$string('general.country_selector.prompt_currency',
                            { replace: { currency: suggestedCurrencyLabel } }
            )"
          />
          <li
            v-if="$string('general.country_selector.prompt_shipping') !== 'No string found'"
            class="text-body-l-regular text-body-xl-regular-desktop"
            v-text="$string('general.country_selector.prompt_shipping',
                            { replace: { country: suggestedCountryNameFormatted } }
            )"
          />
          <li
            v-if="$string('general.country_selector.prompt_language') !== 'No string found'"
            class="text-body-l-regular text-body-xl-regular-desktop"
            v-text="$string('general.country_selector.prompt_language',
                            { replace: { language: suggestedLanguageName } }
            )"
          />
        </ul>
      </div>
    </div>

    <!-- Footer -->
    <div
      v-if="!showPrompt"
      class="country-selector__footer mt-l mt-xl-desktop"
    >
      <btn
        class="country-selector__button"
        :label="$string('general.country_selector.selector_cta')"
        modifiers="primary block center"
        name="country-selector"
        :show-icon="false"
        type="button"
        @click="handleCountrySelectSubmit"
      />

      <span
        tabindex="0"
        class="country-selector__button text-link"
        @click="handleCloseOverlay"
        v-text="$string('general.country_selector.selector_cancel')"
      />
    </div>

    <div
      v-if="showPrompt"
      class="country-selector__footer mt-l mt-xl-desktop"
    >
      <btn
        class="country-selector__button"
        :label="$string('general.country_selector.prompt_continue',
                        { replace: { country: currentCountry } }
        )"
        modifiers="primary block center"
        :show-icon="false"
        type="button"
        @click="handleCloseOverlay"
      />

      <btn
        class="country-selector__button"
        :label="$string('general.country_selector.prompt_change_location')"
        modifiers="secondary block center"
        :show-icon="false"
        type="button"
        @click="handleToggleCountrySelector"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Country selector (country-selector)
 * -----------------------------------------------------------------------------
 * Country selector modal.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
// eslint-disable-next-line import/no-unresolved
import Cookies from 'js-cookie'
import { mapActions } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'
import IconClose from '~icons/directional-navigation/close.svg'
import Btn from '~global/btn/btn'

import locationizationsQuery from '~graphql/localization.gql'

import { convertToCamelCase, removeTrailingSlash } from '~/helpers/convert'

export default {
  name: 'CountrySelector',

  components: {
    Btn,
    IconClose,
    ResponsiveImage,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },

    redirects: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      availableLanguages: [],
      redirectLocales: [],
      currenctCurrencyLabel: `${cnvs.store.currency} ${cnvs.store.currencySymbol}`,
      currentCountry: cnvs.store.country.toLowerCase(),
      currentCountryName: cnvs.store.countryName,
      currentLanguage: cnvs.store.language.toLowerCase(),
      defaultAvailableLanguages: cnvs.strings.defaultLocalization.languages,
      detectedValues: {},
      flagIcon: cnvs.strings.defaultLocalization.flag_svg,
      locales: [],
      localizedShopDomain: cnvs.store.localisedShopDomain,
      shopDomain: cnvs.store.shopDomain,
      showPrompt: null,
      state: 'loading',
      suggestedCountryNameFormatted: '',
      suggestedCountryName: '',
      suggestedCurrencyLabel: '',
      suggestedLanguageName: '',
      updatedLocale: '',
      selectedFlag: null,
      redirectflags: {},
      matchSuggestedMarket: false,
    }
  },

  computed: {

    /**
     * Get the localizedDismissed cookie
     */
    getLocalizedDimissed() {
      return Cookies.get('localizedDismissed')
    },

    /**
     * Get the selected flag
     */
    getSelectedFlag() {
      let selectedCountryIsoCode = this.selectedFlag || this.selectedCountryIsoCode.toUpperCase()

      if (this.locales.length > 0) {
        const findFlag = this.locales.some((country) => {
          return country.isoCode.toUpperCase() === this.selectedCountryIsoCode.toUpperCase()
        })

        if (!findFlag) {
          selectedCountryIsoCode = this.locales[0].isoCode
        }
      }

      return cnvs.strings.defaultLocalization.available_flags[selectedCountryIsoCode] ||
        this.redirectflags[selectedCountryIsoCode]
    },
  },

  watch: {
    state() {
      this.updateAvailableLanguages()
    },

    selectedCountryIsoCode() {
      this.updateAvailableLanguages()
    },
  },

  beforeMount() {
    this.selectedLanguage = this.currentLanguage
    this.selectedCountryIsoCode = this.currentCountry
    this.setInitialLocale()
    this.getDetectedBrowsingContext()
    this.handlePrompt()
  },

  async mounted() {
    await this.queryAllLocalizations()
    this.formatRedirects()
    this.formatSuggestedCountry()
    this.updateAvailableLanguages()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Set initial locale
     */
    setInitialLocale() {
      this.cacheLocale = `${this.currentLanguage}-${this.currentCountry}`
    },

    /**
     * Get location from detected browsing context
     */
    getDetectedBrowsingContext() {
      this.detectedValues = cnvs.strings.defaultLocalization.detected_values || false
    },

    /**
     * Format suggested country info
     */
    formatSuggestedCountry() {
      if (!this.detectedValues) {
        return
      }

      this.suggestedCountryName = this.detectedValues.country.name
      const united = this.$string('general.country_selector.prompt_country_united').toLowerCase()

      if (this.detectedValues.country.name.toLowerCase().includes(united)) {
        const prefix = this.$string('general.country_selector.prompt_country_prefix_united')
          ? `${this.$string('general.country_selector.prompt_country_prefix_united')} `
          : ''
        this.suggestedCountryNameFormatted = `${prefix}${this.detectedValues.country.name}`
      } else {
        this.suggestedCountryNameFormatted = this.detectedValues.country.name
      }

      const suggested = this.locales.find((country) => {
        return country.isoCode === this.detectedValues.country.handle
      })

      this.matchSuggestedMarket = suggested
      this.suggestedCurrencyLabel = suggested ? `${suggested.currency.isoCode} ${suggested.currency.symbol}` : null
      this.suggestedLanguageName = suggested ? suggested.availableLanguages[0].endonymName : null
      this.selectedCountryIsoCode = this.detectedValues.country.handle
    },

    /**
     * Handle the country selector prompt
     */
    handlePrompt() {
      const localizedDismissed = Cookies.get('localizedDismissed')
      if (!localizedDismissed) {
        this.showPrompt = true
        return
      }

      const cookieSplit = localizedDismissed.split('-')
      const cookieDomain = cookieSplit[1]

      if (!this.detectedValues || cookieDomain === this.currentCountry) {
        this.showPrompt = false
        return
      }

      const browsingContextHandle = this.detectedValues.country.handle || false

      if (!browsingContextHandle) {
        this.showPrompt = false
        return
      }

      this.showPrompt = browsingContextHandle !== cnvs.store.country
    },

    /**
     * Handle close overlay
     */
    handleCloseOverlay() {
      this.closeOverlay('countrySelector')
      this.handleDismissLocalizationPrompt()
    },

    /**
     * Handle dismiss prompt
     */
    handleDismissLocalizationPrompt() {
      Cookies.set('localizedDismissed', this.cacheLocale, { expires: 365 })
      this.handlePrompt()
    },

    /**
     * Handle toggle between prompt and country selector
     */
    handleToggleCountrySelector() {
      const detectedLocaleMatch = this.locales.some((locale) => {
        return locale.isoCode === this.selectedCountryIsoCode
      })

      if (!detectedLocaleMatch) {
        this.selectedCountryIsoCode = this.locales[0].isoCode
        this.updateAvailableLanguages()
      }

      this.showPrompt = !this.showPrompt
    },

    /**
     * Handle coutnry selector cta.
     * @param {Object} event - click event
     */
    handleCountrySelectSubmit() {
      if (this.redirectLocales.length > 0) {
        const selectedRedirect = this.redirectLocales.find((el) => {
          return el.isoCode === this.selectedCountryIsoCode
        })

        if (selectedRedirect) {
          this.updateRedirectUrl(selectedRedirect)
          return
        }
      }

      const primary = cnvs.strings.defaultLocalization.primaryMarket
      const newCountryCode = `-${this.selectedCountryIsoCode}`.toLowerCase()
      const newLanguageCode = this.selectedLanguage.toLowerCase() === 'pt_pt'
        ? this.selectedLanguage.toLowerCase().replace('_pt', '')
        : this.selectedLanguage.toLowerCase()

      const currentCountry = `-${this.currentCountry}`

      const currentLocale = `${this.currentLanguage}${currentCountry}`
      this.updatedLocale = `${newLanguageCode}${newCountryCode}`
      this.cacheLocale = this.updatedLocale

      const isPrimaryDomain =
        primary.domain.toLowerCase() === this.selectedCountryIsoCode.toLowerCase()
      const isPrimaryLocale = primary.locale.toLowerCase() === newLanguageCode

      if (currentLocale !== this.updatedLocale) {
        if (isPrimaryDomain) {
          this.updatedLocale = this.updatedLocale.replace(newCountryCode, '')

          if (isPrimaryLocale) {
            this.updatedLocale = this.updatedLocale.replace(newLanguageCode, '')
          }
        }

        this.updateLocationUrl()
      }

      this.handleCloseOverlay()
    },

    /**
     * Update external redirect url
     * @param {Object} selectedRedirect - selected redirect
     */
    updateRedirectUrl(selectedRedirect) {
      const selectedRedirectLang = selectedRedirect.availableLanguages.find((el) => {
        return el.isoCode.toUpperCase() === this.selectedLanguage.toUpperCase()
      })

      if (selectedRedirectLang) {
        const url = selectedRedirectLang.redirectUrl
        window.location = url
      }
    },

    /**
     * Format external redirects
     * - merges the external redirects with the market localizations
     * - formats the external redirect flags
     */
    formatRedirects() {
      if (this.redirects.length === 0) {
        return
      }

      const formattedRedirects = []
      const flags = {}

      this.redirects.forEach((redirect) => {
        const index = formattedRedirects.findIndex((el) => {
          return el.isoCode === redirect.countryIsoCode.toUpperCase()
        })

        const availableLanguages =
          {
            isoCode: redirect.langCode.toUpperCase(),
            endonymName: redirect.language,
            redirectUrl: redirect.redirectUrl,
          }

        const currency = {
          isoCode: redirect.currencyCode.toUpperCase(),
          symbol: redirect.currencySymbol,
        }

        if (index < 0) {
          const obj = {
            name: redirect.country,
            isoCode: redirect.countryIsoCode.toUpperCase(),
            availableLanguages: [availableLanguages],
            currency,
          }
          formattedRedirects.push(obj)
        } else {
          formattedRedirects[index].availableLanguages.push(availableLanguages)
        }

        if (redirect.flag) {
          flags[redirect.countryIsoCode.toUpperCase()] = redirect.flag
        }
      })

      if (formattedRedirects.length > 0) {
        this.redirectLocales = formattedRedirects
        const mergeLocales = this.locales.concat(formattedRedirects)

        this.locales = mergeLocales
        this.redirectflags = flags
      }
    },

    /**
     * Update location url with locale subfolder path
     */
    updateLocationUrl() {
      const newShopDomain = removeTrailingSlash(`${this.shopDomain}${this.updatedLocale}`)
      const newUrl = window.location.href
        .replace(removeTrailingSlash(this.localizedShopDomain), newShopDomain)

      window.location = newUrl
    },

    /**
     * Query all localization data with graphql
     */
    queryAllLocalizations() {
      return new Promise(async(resolve, reject) => {
        try {
          const response = await cnvs.Query({
            query: locationizationsQuery,
          })

          this.locales = response.localization.availableCountries
          this.state = 'ready'
          resolve()

        } catch (error) {
          cnvs.ReportError('Failed to load localizations', error)
          reject(error)
        }
      })
    },

    /**
     * Handle locale selector
     * @param {Object} event - data from select option change
     */
    handleLocaleSelect(event) {
      const selectedLocale = convertToCamelCase(event.target.name.replaceAll('_', ' '))
      this[selectedLocale] = event.target.value

      if (selectedLocale === 'selectedCountryIsoCode') {
        this.selectedFlag = this[selectedLocale]
        this.updateAvailableLanguages()
      }
    },

    /**
     * Update available language options
     */
    updateAvailableLanguages() {
      const languages = this.locales.find((country) => {
        return country.isoCode.toLowerCase() === this.selectedCountryIsoCode.toLowerCase()
      })

      if (!languages) {
        return
      }

      this.availableLanguages = languages.availableLanguages

      const hasSelectedLanguage = this.availableLanguages.some((lang) => {
        return lang.isoCode.toLowerCase() === this.selectedLanguage.toLowerCase()
      })

      if (hasSelectedLanguage) {
        return
      }

      this.selectedLanguage = this.availableLanguages[0].isoCode
    },

    /**
     * Update selected option state.
     * @param {String} current - current option
     * @param {String} selected - selected option
     * @returns {Boolean}
     */
    getSelectedState(current, selected) {
      return current === selected
    },
  },
}
</script>

<style lang="scss">
@import './country-selector';
</style>
